/**
 * PercentoBar — per-row category breakdown bars.
 *
 * Pixel-faithful port of the `CatBarRow` layout from
 * `design/project/screens-analytics.jsx`. Each category is one row with:
 *   - 28x28 colored swatch (icon position)
 *   - category name
 *   - amount + percentage
 *   - a horizontal bar whose width = pct% (offset by swatch margin)
 *
 * The grouping algorithm (top-N + 「其他」) is delegated to `buildPercento`
 * in `@/lib/analytics/percento`. This component renders only.
 *
 * Props:
 *   - `result`: `PercentoResult` with `slices` and optional `otherSlice`.
 *   - `onSelectSlice(id)`: row-tap callback. Called with the slice's
 *     category_id, or `OTHER_SLICE_ID` for the「其他」aggregate.
 *   - `selectedId`: highlight state.
 *   - `variant`: 'card' (default) wraps the rows in a surface card; 'plain'
 *     just emits the rows (used by HomeRightRail which already provides a
 *     surface).
 *   - `categories`: optional category lookup for icon rendering. When
 *     omitted the swatch is plain colored.
 *
 * Design intent: in the AnalyticsScreen the「其他」aggregate is rendered
 * separately by the parent as a collapsible roll-up of <5% slices. To keep
 * PercentoBar single-responsibility, when `result.otherSlice` is present
 * we still render it as the last row (so HomeRightRail still shows it),
 * but AnalyticsScreen passes a result with `otherSlice = null` and renders
 * its own roll-up (because it needs to compute <5% specifically, not just
 * top-N rest).
 */
import { useMemo } from 'react';

import Icon, { type IconName } from '@/components/common/Icon';
import { formatAmount } from '@/lib/format/currency';
import type { CategoryRow } from '@/lib/sheets/types';
import type { CategorySlice, PercentoResult } from '@/lib/analytics/percento';

export const OTHER_SLICE_ID = '__other__';

export interface PercentoBarProps {
  /**
   * Aggregated result. Currency is read from `result.currency` so the
   * legend label always matches the slices' currency — there is no
   * separate prop to drift out of sync.
   */
  result: PercentoResult;
  onSelectSlice?: (categoryId: string | null) => void;
  selectedId?: string | null;
  /** Optional category lookup for icon resolution on each row. */
  categories?: CategoryRow[];
  /** Render mode. Defaults to 'plain' (no outer card). */
  variant?: 'plain' | 'card';
}

export default function PercentoBar({
  result,
  onSelectSlice,
  selectedId,
  categories,
  variant = 'plain',
}: PercentoBarProps): JSX.Element {
  const currency = result.currency;
  const allSlices = useMemo<CategorySlice[]>(() => {
    return result.otherSlice ? [...result.slices, result.otherSlice] : result.slices;
  }, [result.slices, result.otherSlice]);

  const catLookup = useMemo(() => {
    const m = new Map<string, CategoryRow>();
    if (categories) for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const handle = (id: string): void => {
    if (!onSelectSlice) return;
    onSelectSlice(selectedId === id ? null : id);
  };

  const containerStyle =
    variant === 'card'
      ? {
          background: 'var(--bg-surface)',
          borderRadius: 14,
          padding: '4px 14px',
          boxShadow: 'var(--shadow-1)',
        }
      : undefined;

  return (
    <div style={containerStyle}>
      {allSlices.map((s, i) => (
        <div
          key={s.category_id}
          style={{
            borderBottom:
              i < allSlices.length - 1 ? '0.5px solid var(--border-1)' : '0',
          }}
        >
          <CatBarRow
            slice={s}
            isSelected={selectedId === s.category_id}
            isOther={s.category_id === OTHER_SLICE_ID}
            currency={currency}
            iconName={resolveIcon(s, catLookup)}
            onClick={() => handle(s.category_id)}
          />
        </div>
      ))}
    </div>
  );
}

function resolveIcon(
  slice: CategorySlice,
  catLookup: Map<string, CategoryRow>,
): IconName {
  if (slice.category_id === OTHER_SLICE_ID) return 'dots';
  const cat = catLookup.get(slice.category_id);
  const raw = cat?.icon ?? '';
  // best-effort name match; fall back to 'tag'
  return (raw && (raw as IconName)) || 'tag';
}

interface CatBarRowProps {
  slice: CategorySlice;
  isSelected: boolean;
  isOther: boolean;
  currency: string;
  iconName: IconName;
  onClick: () => void;
}

/**
 * One row: swatch / name / amount / pct%, with a bar underneath whose width
 * is `pct%`. The bar starts after the swatch (left margin = 38).
 */
export function CatBarRow({
  slice,
  isSelected,
  isOther,
  currency,
  iconName,
  onClick,
}: CatBarRowProps): JSX.Element {
  const color = isOther ? 'var(--sand-400)' : `var(${slice.color_token})`;
  const widthPct = Math.max(2, slice.pct);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      aria-label={`${slice.name} ${slice.pct.toFixed(0)}%`}
      style={{
        width: '100%',
        textAlign: 'left',
        border: 0,
        background: isSelected ? 'var(--cream-100)' : 'transparent',
        padding: '10px 0',
        cursor: 'pointer',
        display: 'block',
        font: 'inherit',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 6,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: color,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={iconName} size={14} stroke="#fff" />
        </span>
        <span
          style={{
            flex: 1,
            font: '500 14px var(--font-cjk)',
            color: 'var(--fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {slice.name}
        </span>
        <span
          style={{
            font: '500 13px var(--font-num)',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--fg-2)',
          }}
        >
          {formatAmount(slice.total_minor, currency)}
        </span>
        <span
          style={{
            minWidth: 42,
            textAlign: 'right',
            font: '600 13px var(--font-num)',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--fg-1)',
          }}
        >
          {slice.pct.toFixed(0)}%
        </span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: 'var(--cream-200)',
          overflow: 'hidden',
          marginLeft: 38,
        }}
      >
        <div
          style={{
            width: `${widthPct}%`,
            height: '100%',
            background: color,
            borderRadius: 3,
          }}
        />
      </div>
    </button>
  );
}
